import { formatPoints, OPTIONAL_CONSENTS } from "@iec-id/shared";
import { enrollInWebPush } from "@iec-id/shared/utils/web-push-client";
import { Button, Checkbox, Icon, type IconName, IconTile, LinkButton } from "@iec-id/ui";
import { useEffect, useRef, useState } from "react";
import { RewardCallout } from "@/components/shared/reward-callout";
import { ID_WEB_URL } from "@/lib/auth-flow";
import { useLocale } from "@/lib/locale";
import { api } from "@/lib/trpc";
import { useOnboardingT } from "../onboarding-i18n";
import { StepTitle } from "../step-title";
import type { PushOffer } from "../use-push-offer";

/**
 * Воркер лежит в корне домена входа: его область — весь сайт. Адрес витрины
 * уезжает параметром, потому что ссылки в уведомлении сервер шлёт
 * относительными, а `/notifications` на `auth.iec.exchange` не существует
 * (подробности — в шапке `public/sw.js`).
 */
const SERVICE_WORKER_URL = `/sw.js?home=${encodeURIComponent(ID_WEB_URL)}`;

/** Что именно приходит push'ом — три причины, а не абстрактное «уведомления». */
const REASONS = [
	{ key: "security", icon: "ShieldCheck" },
	{ key: "points", icon: "Coins" },
	{ key: "profile", icon: "UserRound" },
] as const satisfies readonly { key: string; icon: IconName }[];

/**
 * Шаг 5 — уведомления и рассылки (решение владельца 23.08.2026).
 *
 * ── ПОЧЕМУ ЭТО ШАГ, А НЕ ЭКРАН ПОСЛЕ ФИНАЛА ────────────────────────────────
 *
 * Раньше предложение push показывалось ПОСЛЕ экрана с начисленными Баллами, то
 * есть после того, как человек мысленно закончил. Теперь от этого экрана
 * зависит награда, и стоять он обязан до финала — со своим номером и полосой
 * прогресса, как все остальные шаги.
 *
 * ── ПОЧЕМУ ЗДЕСЬ ЖЕ СОГЛАСИЯ ───────────────────────────────────────────────
 *
 * Приветственные 300 Баллов выдаются за включённый push И все необязательные
 * согласия. Спрашивать их в двух разных местах значит сделать условие
 * невыполнимым по невнимательности: человек честно нажал «Включить», а Баллы не
 * пришли, потому что галочка осталась на шаге документов.
 *
 * Галочки РАЗДЕЛЬНЫЕ, по одной на цель обработки: рассылка о продукте,
 * рекомендации и новости — три разных основания (GDPR ст. 7(2)), и склеивать
 * их в одну значит терять granularity согласия. Все выключены по умолчанию —
 * преднажатая галочка согласием не является (Planet49, C-673/17).
 *
 * ── БРАУЗЕР БЕЗ PUSH ───────────────────────────────────────────────────────
 *
 * iOS до 16.4 и часть встроенных браузеров web-push не умеют. Такому человеку
 * экран показывается всё равно — согласия ему доступны, — но вместо кнопки в
 * никуда он видит объяснение. Тупик здесь был бы хуже отсутствия экрана:
 * задача «включите уведомления» висела бы невыполнимой не по его вине.
 */
export function NotificationsStep({
	offer,
	rewardPoints,
	onDone,
}: {
	offer: PushOffer;
	/** Сколько Баллов ждёт за выполненную задачу. Считает сервер. */
	rewardPoints: number;
	/** Дальше по маршруту мастера — финальный экран. */
	onDone: () => void;
}) {
	const t = useOnboardingT();
	const locale = useLocale();
	const [busy, setBusy] = useState(false);
	const [failure, setFailure] = useState<string | null>(null);
	const registerPush = api.notifications.registerPush.useMutation();
	const setOptional = api.consents.setOptional.useMutation();

	/**
	 * Действующие согласия читаем с сервера: человек мог включить рассылку
	 * раньше (шаг документов до 23.08.2026, витрина, повторный заход в мастер).
	 * Показать ему пустые галочки значит предложить выключить то, что он уже
	 * включил, — и отправкой шага действительно выключить.
	 */
	const optional = api.consents.optional.useQuery();
	const [checked, setChecked] = useState<Record<string, boolean>>({});
	const [touched, setTouched] = useState(false);

	useEffect(() => {
		if (touched || !optional.data) return;
		setChecked(Object.fromEntries(optional.data.items.map((item) => [item.key, item.enabled])));
	}, [touched, optional.data]);

	// `onDone` приходит из маршрута новой ссылкой на каждый рендер и в
	// зависимостях эффекта не годится.
	const doneRef = useRef(onDone);
	doneRef.current = onDone;

	const pushOffered = offer.status !== "skip";

	// Согласие одно (23.08.2026), и Баллы дают за пару «push + оно».
	const consentKey = OPTIONAL_CONSENTS[0];
	const consentGiven = checked[consentKey] === true;

	/** Сохранить согласия и уйти дальше. Зовётся на обоих исходах шага. */
	async function saveConsentsAndLeave(): Promise<void> {
		const saved = optional.data?.items ?? [];
		await Promise.all(
			OPTIONAL_CONSENTS.map(async (key) => {
				const next = checked[key] ?? false;
				const current = saved.find((item) => item.key === key)?.enabled ?? false;
				// Повтор того же решения сервер и так не запишет, но лишний запрос
				// в конце мастера задерживает возврат в сервис.
				if (next === current) return;
				await setOptional.mutateAsync({ key, enabled: next });
			}),
		);
		doneRef.current();
	}

	async function enable(): Promise<void> {
		setBusy(true);
		setFailure(null);
		const result = await enrollInWebPush({
			serviceWorkerUrl: SERVICE_WORKER_URL,
			publicKey: offer.publicKey,
		});
		if (!result.ok) {
			setBusy(false);
			// Отказ браузера — не ошибка человека: говорим, где включить потом,
			// и оставляем кнопку продолжения.
			setFailure(result.reason === "denied" ? t("push.denied") : t("push.failed"));
			return;
		}
		try {
			await registerPush.mutateAsync(result.subscription);
			await saveConsentsAndLeave();
		} catch {
			// Подписка в браузере есть, а сервер о ней не узнал — уведомления не
			// придут. Молчать здесь нельзя: человек считал бы, что включил.
			setBusy(false);
			setFailure(t("push.failed"));
		}
	}

	function skip(): void {
		setBusy(true);
		setFailure(null);
		saveConsentsAndLeave().catch(() => {
			setBusy(false);
			setFailure(t("push.failed"));
		});
	}

	return (
		<>
			{/* Крупного знака у шага нет — как и у остальных пяти. Он был здесь
			    единственным на весь мастер и читался сменой оформления посреди
			    одинаковой последовательности. */}
			<StepTitle>{t("notifications.title")}</StepTitle>

			<p className="t-body text-fg-secondary">{t("push.body")}</p>

			<ul className="flex flex-col gap-2.5">
				{REASONS.map((reason) => (
					<li key={reason.key} className="flex items-center gap-2.5">
						{/* 40/20 — тот же размер, что у подарка в `RewardCallout` ниже:
						    на одном экране две шкалы плиток читались как разный вес
						    строк, хотя это просто перечисление. */}
						<IconTile size={40}>
							<Icon name={reason.icon} size={20} />
						</IconTile>
						<span className="t-meta text-fg-secondary">{t(`push.reasons.${reason.key}`)}</span>
					</li>
				))}
			</ul>

			<RewardCallout
				title={t("notifications.reward", {
					count: rewardPoints,
					points: formatPoints(rewardPoints, locale),
				})}
				caption={t("notifications.rewardCaption")}
			/>

			{failure ? (
				<p role="alert" className="t-meta text-danger">
					{failure}
				</p>
			) : null}

			{pushOffered ? null : (
				// Кнопки нет — объясняем ПОЧЕМУ именно. «Браузер не умеет» и «вы
				// уже включили» — разные сообщения, и показать первое второму
				// значит соврать человеку в лицо.
				<p className="t-meta text-fg-muted">{t(`notifications.push.${offer.reason}`)}</p>
			)}

			{/* Согласие стоит вплотную к кнопке и оформлено как галочки шага
			    документов — плоской строкой на белом, без утопленной панели:
			    это то же самое действие «отметить и продолжить», и разное
			    оформление читалось как разный по смыслу блок. */}
			<div className="flex flex-col gap-3.5">
				{OPTIONAL_CONSENTS.map((key) => (
					<Checkbox
						key={key}
						checked={checked[key] === true}
						disabled={busy || optional.isPending}
						onCheckedChange={(next) => {
							setTouched(true);
							setChecked((previous) => ({ ...previous, [key]: next }));
						}}
						label={<span className="t-body text-fg">{t(`notifications.consents.${key}`)}</span>}
					/>
				))}
			</div>

			<div className="flex flex-col gap-2.5">
				{pushOffered ? (
					<Button
						// Якорь для браузерных сценариев: подписи переводятся на 21
						// язык, а сам блок кнопки показывается не всегда.
						data-testid="push-enable"
						variant="primary"
						fullWidth
						iconLeft={<Icon name="Bell" />}
						loading={busy || offer.status === "loading"}
						// Баллы дают за пару «push + согласие», поэтому включать push
						// без галочки бессмысленно: человек нажал бы, отдал разрешение
						// браузеру и награды не получил.
						disabled={!consentGiven || optional.isPending}
						onClick={() => void enable()}
					>
						{t("push.enable")}
					</Button>
				) : (
					<Button
						data-testid="push-continue"
						variant="primary"
						fullWidth
						loading={busy}
						onClick={skip}
					>
						{t("actions.continue")}
					</Button>
				)}
				{pushOffered ? (
					<LinkButton data-testid="push-skip" className="self-center text-fg-muted" onClick={skip}>
						{t("push.later")}
					</LinkButton>
				) : null}
			</div>
		</>
	);
}
